import { useAuthStore } from '../store/useAuthStore';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    View, Text, TouchableOpacity, StyleSheet,
    ActivityIndicator, TextInput,
    ScrollView, Platform,
    useWindowDimensions,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AudioPromptButton, speakText } from './AudioPromptButton';
import { useTwoTapConfirm } from '../hooks/useTwoTapConfirm';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { ChildFriendlyBackground } from './ChildFriendlyBackground';
import { fetchWithRetry } from '../utils/fetchWithRetry';

const ROSTER_CACHE_KEY = 'eduwrite_cached_roster';
const ROSTER_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface RosterStudentEntry {
    id: string;
    name: string;
    avatarUrl: string | null;
    groupId: string | null;
    keyStage?: string | null;
    isGroup?: boolean;
}

interface ApiStudent {
    id: string | number;
    name: string;
    avatarUrl?: string;
    groupId?: string | number | null;
    keyStage?: string | null;
}

interface RosterPickerProps {
    classId: string;
    syncServerUrl: string;
    isJoining?: boolean;
    joiningStudentId?: string | null;
    onSelectStudent: (student: RosterStudentEntry) => void;
    onTypeManually: (initialName?: string) => void;
}

const AVATAR_EMOJI = ['🐶', '🐱', '🐻', '🦊', '🐸', '🦋', '🐧', '🦄', '🦁', '🐯', '🐺', '🐨'];

const AVATAR_PALETTES = [
    { bg: '#FEF3C7', border: '#FDE047', iconBg: '#FEF9C3' }, // Amber / Yellow
    { bg: '#DCFCE7', border: '#86EFAC', iconBg: '#F0FDF4' }, // Emerald / Green
    { bg: '#E0F2FE', border: '#7DD3FC', iconBg: '#F0F9FF' }, // Sky Blue
    { bg: '#F3E8FF', border: '#D8B4FE', iconBg: '#FAF5FF' }, // Purple
    { bg: '#FFEDD5', border: '#FDBA74', iconBg: '#FFF7ED' }, // Orange
    { bg: '#FCE7F3', border: '#F472B6', iconBg: '#FDF2F8' }, // Pink
];

const hashCode = (str: string) => {
    let h = 0;
    const s = String(str || '');
    for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
    return h;
};

const avatarEmoji = (name: string) => {
    const s = String(name || '');
    return String(AVATAR_EMOJI[Math.abs(hashCode(s)) % AVATAR_EMOJI.length] || '👤');
};

const avatarPalette = (name: string) => {
    const s = String(name || '');
    return AVATAR_PALETTES[Math.abs(hashCode(s)) % AVATAR_PALETTES.length];
};

export const RosterPicker: React.FC<RosterPickerProps> = ({
    classId,
    syncServerUrl,
    isJoining = false,
    joiningStudentId = null,
    onSelectStudent,
    onTypeManually,
}) => {
    const { width } = useWindowDimensions();
    const [roster, setRoster] = useState<RosterStudentEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const scrollViewRef = useRef<ScrollView>(null);

    const [_isRefreshing, setIsRefreshing] = useState(false);

    /**
     * Voice-first selection — Reception (pre-reader) accommodation.
     *
     * Uses useTwoTapConfirm so the first tap speaks the name and highlights
     * the card; the second tap confirms. Auto-cancels after timeout.
     *
     * WP-033: students no longer self-select tables — the roster is
     * students-only. The `onSelectStudent` callback only ever receives
     * individual students (never `isGroup: true`).
     */
    const { handleTap, isPending } = useTwoTapConfirm({
        timeoutMs: 6000,
        onFirstTap: (id) => {
            const entry = roster.find((e) => String(e.id) === String(id) || e.name.toLowerCase() === String(id).toLowerCase());
            if (entry) {
                speakText(`${entry.name}. Tap again to start.`);
            }
        },
        onConfirm: (id) => {
            const entry = roster.find((e) => String(e.id) === String(id) || e.name.toLowerCase() === String(id).toLowerCase());
            if (entry) onSelectStudent(entry);
        },
    });

    const handleEntryPress = useCallback(
        (entry: RosterStudentEntry) => {
            if (!isJoining) handleTap(entry.id);
        },
        [handleTap, isJoining]
    );

    /**
     * Speech-to-text voice search — "Say your name" button.
     *
     * Student taps the mic button, speaks their name, and the app
     * highlights the best-matching roster entry (setting it as pending).
     * The student then taps the highlighted card again to confirm.
     */
    const [voiceError, setVoiceError] = useState<string | null>(null);
    const [spokenName, setSpokenName] = useState<string | null>(null);

    const { state: voiceState, isAvailable: voiceAvailable, startListening, stopListening } = useVoiceInput({
        onResult: (spoken) => {
            if (!spoken) {
                setVoiceError('Did not hear anything. Try again!');
                setSpokenName(null);
                return;
            }
            setVoiceError(null);
            const spokenLower = spoken.toLowerCase().trim();
            const match = roster.find((entry) =>
                entry.name.toLowerCase() === spokenLower ||
                spokenLower.includes(entry.name.toLowerCase()) ||
                entry.name.toLowerCase().includes(spokenLower)
            );
            if (match) {
                setSpokenName(null);
                setSearch(match.name);
                handleTap(match.id);
                speakText(`${match.name}. Tap again to start.`);
                scrollViewRef.current?.scrollTo({ y: 0, animated: true });
            } else {
                setSpokenName(spoken);
                setSearch(spoken);
                setVoiceError(`"${spoken}"? No match on the list. Tap below to join with that name.`);
                speakText(`No match for ${spoken}. Tap the button to join with that name.`);
                scrollViewRef.current?.scrollTo({ y: 0, animated: true });
            }
        },
        onError: (msg) => {
            const isRecognitionMessage = msg === 'Did not hear anything. Try again!' || msg === 'Could not hear your name. Try again!';
            const userMessage = isRecognitionMessage ? msg : 'Cannot start listening. Check microphone permission.';
            console.error('[RosterPicker] Voice search error:', msg);
            setVoiceError(userMessage);
            setSpokenName(null);
            speakText(userMessage);
        },
    });

    const handleVoiceSearch = useCallback(async () => {
        if (voiceState === 'listening') {
            await stopListening();
            return;
        }
        setVoiceError(null);
        setSpokenName(null);
        try {
            await startListening();
        } catch (error) {
            console.error('[RosterPicker] Unable to start voice search:', error);
            setVoiceError('Cannot start listening. Check microphone permission.');
            speakText('Cannot start listening. Check microphone permission.');
        }
    }, [voiceState, startListening, stopListening]);

    // Load cached roster data first for instant display
    const loadCachedRoster = useCallback(async () => {
        try {
            const cached = await AsyncStorage.getItem(`${ROSTER_CACHE_KEY}_${classId}`);
            if (cached) {
                const { students, timestamp } = JSON.parse(cached);
                const isValid = Date.now() - timestamp < ROSTER_CACHE_TTL_MS;

                if (isValid) {
                    setRoster(students);
                    return true;
                }
            }
        } catch (e) {
            console.error('[RosterPicker] Failed to load cached roster:', e);
        }
        return false;
    }, [classId]);

    // Save roster to cache
    const saveRosterCache = async (students: RosterStudentEntry[]) => {
        try {
            await AsyncStorage.setItem(
                `${ROSTER_CACHE_KEY}_${classId}`,
                JSON.stringify({ students, timestamp: Date.now() })
            );
        } catch (e) {
            console.error('[RosterPicker] Failed to cache roster:', e);
        }
    };

    const fetchData = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true);
        else setIsRefreshing(true);

        setError(null);
        try {
            const apiBase = String(syncServerUrl).endsWith('/') ? String(syncServerUrl).slice(0, -1) : String(syncServerUrl);
            const studentsUrl = `${apiBase}/api/classes/${classId}/students`;

            const deviceToken = useAuthStore.getState().deviceToken;
            if (!deviceToken) {
                throw new Error('Session expired. Please rejoin the class.');
            }

            // WP-033: only the students endpoint is fetched. The groups endpoint
            // (/api/classes/:classId/groups) is still used by the teacher portal
            // but students no longer self-select tables — the teacher assigns
            // table membership from the portal.
            const studentRes = await fetchWithRetry(studentsUrl, {
                headers: { Authorization: `Bearer ${deviceToken}` },
                timeoutMs: 8000,
                maxRetries: 3,
            }).catch(err => {
                console.error('[RosterPicker] Final Fetch Error:', err);
                throw new Error('Connection failed. Are you on the right network?');
            });

            if (!studentRes.ok) throw new Error(`Class Code Error (${studentRes.status})`);

            const studentsDataRaw = await studentRes.json();
            const studentsData = Array.isArray(studentsDataRaw) ? studentsDataRaw : (studentsDataRaw?.students || []);

            const formattedStudents = studentsData.map((s: ApiStudent) => ({
                id: String(s.id || ''),
                name: String(s.name || 'Student'),
                avatarUrl: s.avatarUrl || null,
                groupId: s.groupId ? String(s.groupId) : null,
                keyStage: s.keyStage || null,
                isGroup: false
            }));

            setRoster(formattedStudents);
            await saveRosterCache(formattedStudents);
        } catch (e: any) {
            setError(String((e as Error).message || 'Error loading roster.'));
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [classId, syncServerUrl]);

    useEffect(() => {
        // Try to load cached roster first for instant display
        const init = async () => {
            const hasCache = await loadCachedRoster();
            // If no cache, show loading and fetch
            if (!hasCache) {
                setLoading(true);
            }
            // Always fetch fresh data (silently if we have cache)
            await fetchData(!hasCache);
        };
        init();
    }, [fetchData, loadCachedRoster]);

    const filteredStudents = roster.filter(s =>
        String(s.name).toLowerCase().includes(String(search).toLowerCase())
    );

    const nameToJoin = spokenName || (search.trim().length > 0 && filteredStudents.length === 0 ? search.trim() : null);

    // --- RENDER LOGIC (CHILD-FRIENDLY & ACCESSIBLE) ---

    if (loading) {
        return (
            <ChildFriendlyBackground variant="light">
                <View style={styles.center}>
                    <View style={styles.loadingOrb}>
                        <Text style={styles.loadingEmoji} allowFontScaling={true} maxFontSizeMultiplier={1.5}>🦊</Text>
                        <ActivityIndicator testID="loading-indicator" size="large" color="#1890FF" style={styles.spinner} />
                    </View>
                    <Text style={styles.loadingTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Looking for your class...</Text>
                    <Text style={styles.loadingSubtext} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Hang tight while we fetch your friends!</Text>
                    <View style={styles.audioWrapCenter}>
                        <AudioPromptButton text="Looking for your class. Wait a moment." autoPlay />
                    </View>
                </View>
            </ChildFriendlyBackground>
        );
    }

    if (error && roster.length === 0) {
        const errorAudio = `Something went wrong. ${error}. Tap retry or ask your teacher.`;
        return (
            <ChildFriendlyBackground variant="light">
                <View style={styles.center}>
                    <View style={styles.errorOrb}>
                        <Text style={styles.loadingEmoji} allowFontScaling={true} maxFontSizeMultiplier={1.5}>🥺</Text>
                    </View>
                    <Text style={styles.errorTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Oops!</Text>
                    <Text style={styles.errorText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{error}</Text>
                    <View style={styles.audioWrapCenter}>
                        <AudioPromptButton text={errorAudio} autoPlay />
                    </View>
                    <TouchableOpacity style={styles.retryBtn} onPress={() => fetchData(true)} activeOpacity={0.8} accessibilityRole="button" accessibilityLabel="Let's Try Again" accessibilityHint="Double tap to try loading the class list again">
                        <Text style={styles.retryBtnText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Let's Try Again</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.manualEntryLink} onPress={() => onTypeManually()} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel="Type name manually" accessibilityHint="Double tap to type your name instead">
                        <Text style={styles.manualEntryLinkText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Type name manually</Text>
                    </TouchableOpacity>
                </View>
            </ChildFriendlyBackground>
        );
    }

    // Responsive column count:
    // Mobile (<580px): 2 columns
    // Small tablet / portrait (580px - 859px): 3 columns
    // Tablet landscape / standard laptop (860px - 1179px): 4 columns
    // Wide desktop / PWA (>=1180px): 5 columns
    const numColumns = width >= 1180 ? 5 : width >= 860 ? 4 : width >= 580 ? 3 : 2;

    // Manual Row Chunking for students (stable across Android Hermes and responsive across web/tablet)
    const studentRows: RosterStudentEntry[][] = [];
    for (let i = 0; i < filteredStudents.length; i += numColumns) {
        studentRows.push(filteredStudents.slice(i, i + numColumns));
    }

    return (
        <ChildFriendlyBackground variant="light" scrollable={false}>
            <ScrollView
                ref={scrollViewRef}
                style={{ flex: 1 }}
                contentContainerStyle={styles.scrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
            >
                <View style={styles.mainContainer}>
                    {/* Header Title Section: Centered Title & Subtitle with Audio + Controls */}
                    <View style={styles.headerArea}>
                        <Text style={styles.title} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Who are you?</Text>

                        <View style={styles.subtitleRow}>
                            <Text style={styles.subtitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                Tap your name to hear it, then tap again to start
                            </Text>
                            <View style={styles.audioRow}>
                                <AudioPromptButton text="Who are you? Tap your name to hear it, then tap again to start." />
                            </View>
                        </View>

                        {/* Quick Search & Voice Bar */}
                        <View style={styles.controlsCard}>
                            <View style={styles.searchRow}>
                                <TextInput
                                    style={styles.searchInput}
                                    placeholder="Search for your name..."
                                    placeholderTextColor="#94A3B8"
                                    value={search}
                                    onChangeText={(val) => {
                                        setSearch(val);
                                        if (voiceError) setVoiceError(null);
                                        if (spokenName && val !== spokenName) setSpokenName(null);
                                    }}
                                    autoCorrect={false}
                                    clearButtonMode="while-editing"
                                    accessibilityLabel="Search for your name"
                                    accessibilityHint="Type your name to find it in the list"
                                />
                                {Platform.OS !== 'web' && voiceAvailable && (
                                    <TouchableOpacity
                                        style={[styles.voiceBtn, voiceState === 'listening' && styles.voiceBtnActive]}
                                        onPress={handleVoiceSearch}
                                        activeOpacity={0.75}
                                        accessibilityLabel={voiceState === 'listening' ? 'Stop listening' : 'Say your name'}
                                        accessibilityRole="button"
                                        accessibilityHint="Double tap to search by saying your name out loud"
                                    >
                                        <Text style={styles.voiceBtnIcon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{voiceState === 'listening' ? '🎙️' : '🎤'}</Text>
                                        <Text style={[styles.voiceBtnText, voiceState === 'listening' && styles.voiceBtnTextActive]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                            {voiceState === 'listening' ? 'Listening…' : 'Say your name'}
                                        </Text>
                                    </TouchableOpacity>
                                )}
                            </View>

                            {Platform.OS !== 'web' && voiceAvailable && (
                                <Text style={styles.voiceHintText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Can't find your name? Say it out loud!</Text>
                            )}

                            {voiceState === 'listening' && (
                                <View style={styles.listeningBadge}>
                                    <Text style={styles.listeningDot} allowFontScaling={true} maxFontSizeMultiplier={1.5}>🔴</Text>
                                    <Text style={styles.listeningHint} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Say your name out loud</Text>
                                </View>
                            )}

                            {voiceError ? (
                                <View style={styles.voiceErrorBanner}>
                                    <Text style={styles.voiceErrorText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{voiceError}</Text>
                                </View>
                            ) : null}

                            {nameToJoin && (
                                <TouchableOpacity
                                    style={styles.joinSpokenBtn}
                                    onPress={() => onTypeManually(nameToJoin)}
                                    activeOpacity={0.8}
                                    accessibilityLabel={`Join as ${nameToJoin}`}
                                    accessibilityRole="button"
                                    accessibilityHint="Double tap to join the class with this name"
                                >
                                    <Text style={styles.joinSpokenIcon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>👋</Text>
                                    <Text style={styles.joinSpokenText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Join as {nameToJoin}</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>

                    {/* Students list */}
                    <View style={styles.section}>
                        <View style={styles.sectionHeaderRow}>
                            <Text style={styles.sectionTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>STUDENTS</Text>
                            <Text style={styles.studentCountBadge} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                {filteredStudents.length} {filteredStudents.length === 1 ? 'student' : 'students'}
                            </Text>
                        </View>

                        {filteredStudents.length === 0 && (
                            <View style={styles.emptyContainer}>
                                <Text style={styles.emptyEmoji} allowFontScaling={true} maxFontSizeMultiplier={1.5}>🔍</Text>
                                <Text style={styles.emptyText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>No students found</Text>
                                <Text style={styles.emptySubtext} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                    {nameToJoin ? 'Tap the button above to start!' : 'Try typing your name below!'}
                                </Text>
                            </View>
                        )}

                        {studentRows.map((row, rowIndex) => (
                            <View key={`row-${rowIndex}`} style={styles.manualRow}>
                                {row.map((student) => {
                                    const palette = avatarPalette(student.name);
                                    const pending = isPending(student.id);
                                    const joining = joiningStudentId === student.id;
                                    return (
                                        <View key={`std-wrap-${student.id}`} style={styles.studentCardWrapper}>
                                            <TouchableOpacity
                                                style={[
                                                    styles.studentCard,
                                                    (pending || joining) && styles.cardPending,
                                                ]}
                                                onPress={() => handleEntryPress(student)}
                                                disabled={isJoining}
                                                activeOpacity={0.75}
                                                accessibilityLabel={joining ? `Joining ${student.name}` : `Select ${student.name}`}
                                                accessibilityRole="button"
                                                accessibilityHint="Double tap to hear your name, then tap again to start"
                                            >
                                                <View
                                                    style={[
                                                        styles.avatarCircle,
                                                        { backgroundColor: palette.bg, borderColor: palette.border },
                                                        pending && styles.avatarCirclePending,
                                                    ]}
                                                >
                                                    <Text style={styles.avatarEmoji} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{avatarEmoji(student.name)}</Text>
                                                </View>
                                                <Text style={styles.studentName} numberOfLines={1} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                                    {String(student.name)}
                                                </Text>
                                                {student.groupId ? (
                                                    <View style={styles.badge}>
                                                        <Text style={styles.badgeText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{String(student.groupId)}</Text>
                                                    </View>
                                                ) : null}
                                                {joining ? (
                                                    <View style={styles.pendingBadgeWrap}>
                                                        <ActivityIndicator size="small" color="#FFFFFF" />
                                                        <Text style={styles.confirmHint} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Joining...</Text>
                                                    </View>
                                                ) : pending ? (
                                                    <View style={styles.pendingBadgeWrap}>
                                                        <Text style={styles.confirmHint} allowFontScaling={true} maxFontSizeMultiplier={1.5}>🔊 Tap again to start</Text>
                                                    </View>
                                                ) : null}
                                            </TouchableOpacity>
                                        </View>
                                    );
                                })}
                                {/* Spacers for uneven rows */}
                                {Array.from({ length: numColumns - row.length }).map((_, s) => (
                                    <View key={`spacer-${rowIndex}-${s}`} style={styles.studentCardWrapper} />
                                ))}
                            </View>
                        ))}

                        {/* Manual Name Entry Option — integrated cleanly into scroll layout */}
                        <View style={styles.notListedContainer}>
                            <TouchableOpacity
                                style={styles.notListedBtn}
                                onPress={() => onTypeManually()}
                                activeOpacity={0.8}
                                accessibilityLabel="I'm not on the list — type my name"
                                accessibilityRole="button"
                                accessibilityHint="Double tap to type your name manually"
                            >
                                <Text style={styles.notListedIcon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✏️</Text>
                                <Text style={styles.notListedText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>I'm not on the list — type my name</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </ScrollView>
        </ChildFriendlyBackground>
    );
};

const styles = StyleSheet.create({
    center: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 32,
    },
    scrollContent: {
        paddingTop: 12,
        paddingBottom: 48,
    },
    mainContainer: {
        width: '100%',
        maxWidth: 1100,
        alignSelf: 'center',
        paddingHorizontal: 12,
    },
    headerArea: {
        alignItems: 'center',
        paddingTop: 16,
        paddingBottom: 12,
        paddingHorizontal: 8,
    },
    title: {
        fontSize: 34,
        fontWeight: '900',
        color: '#1E293B',
        letterSpacing: -0.5,
        textAlign: 'center',
        marginBottom: 6,
    },
    subtitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 10,
        marginBottom: 16,
    },
    subtitle: {
        fontSize: 16,
        color: '#64748B',
        fontWeight: '700',
        textAlign: 'center',
    },
    audioRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    controlsCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        padding: 12,
        width: '100%',
        maxWidth: 580,
        alignSelf: 'center',
        shadowColor: '#0F172A',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 12,
        elevation: 3,
        borderWidth: 2,
        borderColor: '#DBEAFE',
        borderBottomWidth: 4,
        borderBottomColor: '#BFDBFE',
    },
    searchRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    searchInput: {
        flex: 1,
        backgroundColor: '#F8FAFC',
        borderRadius: 16,
        paddingHorizontal: 16,
        paddingVertical: 12,
        color: '#1E293B',
        fontSize: 16,
        fontWeight: '700',
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
    },
    voiceBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#EEF2FF',
        borderRadius: 14,
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderWidth: 1.5,
        borderColor: '#C7D2FE',
        gap: 6,
    },
    voiceBtnActive: {
        backgroundColor: '#FEE2E2',
        borderColor: '#F87171',
    },
    voiceBtnIcon: {
        fontSize: 18,
    },
    voiceBtnText: {
        fontSize: 14,
        fontWeight: '800',
        color: '#4338CA',
    },
    voiceBtnTextActive: {
        color: '#DC2626',
    },
    voiceHintText: {
        marginTop: 8,
        fontSize: 13,
        fontWeight: '600',
        color: '#64748B',
        textAlign: 'center',
    },
    listeningBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 8,
        paddingVertical: 6,
        paddingHorizontal: 12,
        backgroundColor: '#FEF2F2',
        borderRadius: 20,
        alignSelf: 'center',
        gap: 6,
    },
    listeningDot: {
        fontSize: 10,
    },
    listeningHint: {
        fontSize: 13,
        fontWeight: '700',
        color: '#DC2626',
    },
    voiceErrorBanner: {
        marginTop: 8,
        padding: 10,
        backgroundColor: '#FEF2F2',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#FECACA',
    },
    voiceErrorText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#DC2626',
        textAlign: 'center',
    },
    joinSpokenBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 10,
        paddingHorizontal: 18,
        paddingVertical: 12,
        backgroundColor: '#DCFCE7',
        borderRadius: 14,
        borderWidth: 2,
        borderColor: '#86EFAC',
        gap: 8,
    },
    joinSpokenIcon: {
        fontSize: 20,
    },
    joinSpokenText: {
        fontSize: 15,
        fontWeight: '800',
        color: '#15803D',
    },
    section: {
        width: '100%',
        paddingHorizontal: 4,
        marginTop: 10,
    },
    sectionHeaderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 8,
        marginBottom: 12,
    },
    sectionTitle: {
        fontSize: 14,
        fontWeight: '900',
        color: '#475569',
        letterSpacing: 1.5,
    },
    studentCountBadge: {
        fontSize: 13,
        fontWeight: '800',
        color: '#64748B',
        backgroundColor: '#F1F5F9',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
    },
    manualRow: {
        flexDirection: 'row',
        width: '100%',
        marginBottom: 10,
    },
    studentCardWrapper: {
        flex: 1,
        padding: 6,
    },
    studentCard: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 22,
        paddingHorizontal: 12,
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        borderWidth: 2.5,
        borderColor: '#E2E8F0',
        borderBottomWidth: 5,
        borderBottomColor: '#CBD5E1',
        shadowColor: '#0F172A',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.08,
        shadowRadius: 10,
        elevation: 3,
        minHeight: 180,
    },
    cardPending: {
        borderColor: '#22C55E',
        borderBottomColor: '#16A34A',
        borderWidth: 3,
        borderBottomWidth: 5,
        backgroundColor: '#F0FDF4',
        shadowColor: '#22C55E',
        shadowOpacity: 0.25,
        shadowRadius: 14,
        elevation: 6,
    },
    avatarCircle: {
        width: 82,
        height: 82,
        borderRadius: 41,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 10,
        borderWidth: 3,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 4,
    },
    avatarCirclePending: {
        transform: [{ scale: 1.08 }],
    },
    avatarEmoji: {
        fontSize: 44,
    },
    studentName: {
        fontSize: 17,
        fontWeight: '900',
        color: '#0F172A',
        textAlign: 'center',
    },
    badge: {
        marginTop: 6,
        backgroundColor: '#EFF6FF',
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#BFDBFE',
    },
    badgeText: {
        fontSize: 11,
        fontWeight: '800',
        color: '#2563EB',
    },
    pendingBadgeWrap: {
        marginTop: 8,
        backgroundColor: '#22C55E',
        paddingHorizontal: 12,
        paddingVertical: 5,
        borderRadius: 14,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    confirmHint: {
        fontSize: 13,
        fontWeight: '900',
        color: '#FFFFFF',
        textAlign: 'center',
    },
    notListedContainer: {
        marginTop: 20,
        marginBottom: 32,
        paddingHorizontal: 6,
        alignItems: 'center',
    },
    notListedBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 14,
        paddingHorizontal: 24,
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        borderWidth: 2,
        borderColor: '#CBD5E1',
        borderBottomWidth: 4,
        borderBottomColor: '#94A3B8',
        shadowColor: '#0F172A',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.05,
        shadowRadius: 6,
        elevation: 2,
        gap: 10,
        maxWidth: 440,
        width: '100%',
    },
    notListedIcon: {
        fontSize: 18,
    },
    notListedText: {
        color: '#334155',
        fontSize: 15,
        fontWeight: '800',
    },
    loadingOrb: {
        width: 110,
        height: 110,
        borderRadius: 55,
        backgroundColor: '#FFF7E6',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 20,
        shadowColor: '#FAAD14',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 10,
        elevation: 4,
    },
    loadingEmoji: {
        fontSize: 52,
    },
    spinner: {
        position: 'absolute',
    },
    loadingTitle: {
        fontSize: 24,
        fontWeight: '900',
        color: '#1E293B',
        textAlign: 'center',
        marginBottom: 6,
    },
    loadingSubtext: {
        fontSize: 16,
        color: '#64748B',
        textAlign: 'center',
        fontWeight: '600',
    },
    audioWrapCenter: {
        marginTop: 20,
        alignItems: 'center',
    },
    errorOrb: {
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: '#FEE2E2',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 16,
    },
    errorTitle: {
        fontSize: 26,
        fontWeight: '900',
        color: '#1E293B',
        marginBottom: 8,
    },
    errorText: {
        fontSize: 16,
        color: '#64748B',
        textAlign: 'center',
        lineHeight: 22,
        fontWeight: '600',
        maxWidth: 320,
    },
    retryBtn: {
        marginTop: 24,
        backgroundColor: '#52C41A',
        paddingHorizontal: 32,
        paddingVertical: 16,
        borderRadius: 16,
        shadowColor: '#52C41A',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 4,
    },
    retryBtnText: {
        color: '#FFFFFF',
        fontSize: 17,
        fontWeight: '900',
    },
    manualEntryLink: {
        marginTop: 18,
        padding: 8,
    },
    manualEntryLinkText: {
        color: '#64748B',
        fontSize: 15,
        fontWeight: '700',
        textDecorationLine: 'underline',
    },
    emptyContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 36,
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        marginHorizontal: 6,
        marginBottom: 16,
    },
    emptyEmoji: {
        fontSize: 40,
        marginBottom: 8,
    },
    emptyText: {
        fontSize: 18,
        fontWeight: '800',
        color: '#1E293B',
    },
    emptySubtext: {
        fontSize: 14,
        color: '#64748B',
        marginTop: 4,
        fontWeight: '600',
    },
    joinEmptyBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 14,
        paddingHorizontal: 22,
        paddingVertical: 14,
        backgroundColor: '#DCFCE7',
        borderRadius: 16,
        borderWidth: 2,
        borderColor: '#86EFAC',
        gap: 8,
    },
});

